import {ChangeDetectionStrategy, Component, inject, Input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs';
import {AnalysisService} from '../../services/analysis.service';
import {BarCardComponent} from '@/app/shared/components/bar-card/bar-card.component';
import {MultiDonutCardComponent} from '@/app/shared/components/multi-donut-card/multi-donut-card.component';
import {AnalysisDemographicFilters, FinalAssessmentsApiResponse} from '../../infrastructure/analysis.api.types';
import {Risk} from '@/app/utils/risk-colors';
import {
  BarCardVM,
  buildBarCard,
  buildDonutCard,
  ChartCardVM,
  createBaseTotal$,
  DemographicKey,
  DonutCardVM,
  DonutMini,
  FACET_LABELS,
  processCards,
  readFilterValueForFacet
} from "../../utils/report-chart.utils";
import {EmptyStateComponent} from '../empty-state/empty-state.component';

type ViewModel = {
  donutCards: DonutCardVM[];
  barCards: BarCardVM[];
  isEmpty: boolean;
  loading: boolean;
};

const deepEqual = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

@Component({
  selector: 'app-final-assessments-breakdown',
  standalone: true,
  imports: [CommonModule, BarCardComponent, MultiDonutCardComponent, EmptyStateComponent],
  templateUrl: './final-assessments-breakdown.component.html',
  styleUrls: ['./final-assessments-breakdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FinalAssessmentsBreakdownComponent {
  @Input() showEmpty = false;
  @Input() showLoading = false;
  private analysis = inject(AnalysisService);

  private readonly facetLabels = FACET_LABELS;

  private campaignId$ = new BehaviorSubject<string | null>(null);
  private guideId$ = new BehaviorSubject<string | null>(null);
  private facets$ = new BehaviorSubject<string[]>([]);
  private filters$ = new BehaviorSubject<Partial<AnalysisDemographicFilters>>({});

  private readonly baseTotal$ = createBaseTotal$(this.analysis, this.campaignId$, this.guideId$);

  public readonly vm$: Observable<ViewModel> = combineLatest({
    camp: this.campaignId$.pipe(distinctUntilChanged()),
    guide: this.guideId$.pipe(distinctUntilChanged()),
    facets: this.facets$.pipe(distinctUntilChanged<string[]>((prev, curr) => deepEqual(prev, curr))),
    filters: this.filters$.pipe(
      distinctUntilChanged<Partial<AnalysisDemographicFilters>>((prev, curr) => deepEqual(prev, curr))
    ),
    baseTotal: this.baseTotal$,
  }).pipe(
    filter(({camp, guide}) => !!camp && !!guide),
    switchMap(({camp, guide, facets, filters, baseTotal}) =>
      this.analysis
        .getFinalAssessmentsGrouped({
          campaign: camp!,
          guideId: guide!,
          groupBy: facets,
          ...(filters ?? {}),
        })
        .pipe(
          distinctUntilChanged<any[]>((a, b) => JSON.stringify(a) === JSON.stringify(b)),
          map((rows: any[]) => {
            const cards = this.buildCards(rows as FinalAssessmentsApiResponse, facets as DemographicKey[], filters, baseTotal);
            const { donutCards, barCards, isEmpty } = processCards(cards, rows);
            return {donutCards, barCards, isEmpty, loading: false} as ViewModel;
          }),
          startWith({donutCards: [], barCards: [], isEmpty: false, loading: true} as ViewModel)
        )
    ),
    startWith({donutCards: [], barCards: [], isEmpty: false, loading: false} as ViewModel),
    shareReplay({bufferSize: 1, refCount: true})
  );

  @Input({required: true}) set campaignId(v: string) {
    this.campaignId$.next(v ?? null);
  }

  @Input() set guideId(v: string) {
    this.guideId$.next(v ?? null);
  }

  @Input() set activeFacets(v: string[]) {
    this.facets$.next(Array.isArray(v) ? v : []);
  }

  @Input() set filters(v: Partial<AnalysisDemographicFilters>) {
    this.filters$.next(v ?? {});
  }

  trackByFacet = (_: number, c: ChartCardVM) => c.facet;

  private _rowRisk = (r: any): Risk | null => (r.finalAssessment as Risk) || (r.rating as Risk) || null;

  private buildCards(
    rows: FinalAssessmentsApiResponse,
    facets: DemographicKey[],
    filters: Partial<AnalysisDemographicFilters>,
    baseTotal: number
  ): ChartCardVM[] {
    if (!Array.isArray(rows) || rows.length === 0) return [];

    const out: ChartCardVM[] = [];

    if (facets.length === 0) {
      const title = 'Nivel de Riesgo';
      const baseCard = { facet: 'finalAssessment' as const, title };
      const totalHintFn = (subTotal: number) => `Total: ${subTotal} de ${baseTotal} cuestionarios respondidos.`;
      const donutBuckets = new Map([['', rows]]);
      const seriesFn = (present: { risk: Risk, count: number }[]) => {
        return present.map(x => ({
          name: `${x.count} personas con riesgo ${x.risk.toLowerCase()}`,
          value: x.count,
        }));
      };
      const card = buildDonutCard(baseCard, donutBuckets, this._rowRisk, seriesFn, totalHintFn);
      out.push(card);
      return out;
    }

    for (const facet of facets) {
      const byBucket = new Map<string, any[]>();
      for (const r of rows) {
        const bucketRaw = (r as any)[facet];
        const bucket = this.safeCategory(bucketRaw);
        if (!bucket) continue;
        const arr = byBucket.get(bucket) ?? [];
        arr.push(r);
        byBucket.set(bucket, arr);
      }

      const useBarChart = byBucket.size > 1;
      const facetLabel = this.facetLabels.get(facet) ?? facet;
      const filterValue = readFilterValueForFacet(facet, filters);

      let title = `Nivel de riesgo — ${facetLabel}${filterValue ? `: ${filterValue}` : ''}`;
      if (!useBarChart && byBucket.size === 1) {
        const singleBucketKey = [...byBucket.keys()][0];
        title = `Nivel de riesgo — ${facetLabel}: ${singleBucketKey}`;

      }

      const baseCard = { facet, title };
      const totalHintFn = (subTotal: number) => `Total: ${subTotal} de ${baseTotal} cuestionarios respondidos.`;

      if (useBarChart) {
        const card = buildBarCard(baseCard, rows, byBucket, this._rowRisk, totalHintFn);
        out.push(card);
      } else {
        const donutBuckets = byBucket.size > 0 ? byBucket : new Map([['', rows]]);
        const seriesFn = (present: { risk: Risk, count: number }[], minis: DonutMini[]) => {
          return present.map(x => {
            const breakdownText = minis
              .map(mini => {
                const slice = mini.data.find(s => s.name === x.risk);
                const count = slice?.value ?? 0;
                if (count === 0) return null;
                let bucketLabel = mini.title;
                if (facet === 'gender') {
                  if (mini.title.toLowerCase().startsWith('femenino')) bucketLabel = 'F';
                  else if (mini.title.toLowerCase().startsWith('masculino')) bucketLabel = 'M';
                }
                return `${count} ${bucketLabel}`;
              })
              .filter(Boolean)
              .join(', ');

            const hasBreakdown = minis.length > 1 && breakdownText;
            const breakdown = hasBreakdown ? `(${breakdownText})` : '';
            const name = `${x.count} personas con riesgo ${x.risk.toLowerCase()} ${breakdown}`.trim();
            return { name, value: x.count };
          });
        };
        const card = buildDonutCard(baseCard, donutBuckets, this._rowRisk, seriesFn, totalHintFn);
        out.push(card);
      }
    }

    return out;
  }

  private safeCategory(v: unknown): string | null {
    const s = String(v ?? '').trim();
    if (!s || s === 'null' || s === 'undefined') return null;
    if (s.toLowerCase() === 'true') return 'Sí';
    if (s.toLowerCase() === 'false') return 'No';
    return s;
  }
}
